import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col, Form, InputGroup, Modal, Row,
} from 'react-bootstrap';
import { format } from 'date-fns';

import Accordion from 'components/Accordion';
import DatePicker from 'components/DatePicker';
import Button from 'components/DesignSystem/Button';
import Models from 'components/Windx/ControlPanel/Models';
import Rmv from 'components/Windx/ControlPanel/Rmv';
import RunType from 'components/Windx/ControlPanel/RunType';
import {
  ModeloDisponivel,
  OpcaoRmv,
  PainelOptions,
  WindxSimulacaoAntiga,
} from 'helper/windx';
import {
  selectWindxIdsSimulacoesAntigasAtivas,
  toggleSimulacaoAntigaAtiva,
} from 'redux/reducers/windxControlPanelReducer';
import {
  useGetModelosDisponiveisQuery,
  useSaveSimulacaoAntigaMutation,
} from 'services/api/windx';
import { cssVar } from 'constants/theme';

import useSimulacaoAntigaForm from './useSimulacaoAntigaForm';

const LIMITE_NOME = 30;

// Mesmas cores do Accordion.Header primary (ds-accordion-header-primary)
// para casar o "Data da Simulação" com os accordions Tipo de Rodada /
// Modelos / RMV ao lado.
// Mesmo cabeçalho do Accordion do DS (components/Accordion/Header), em tokens de tema.
const ACCORDION_HEADER_BG = cssVar('bg-muted');
const ACCORDION_HEADER_FG = cssVar('text-strong');

interface ModalCadastrarProps {
  open: boolean;
  initialValue?: WindxSimulacaoAntiga; // undefined = cadastrar; defined = editar
  opts: PainelOptions;
  dataMinima: string; // ISO YYYY-MM-DD (D-14)
  dataMaxima: string; // ISO YYYY-MM-DD (D-1)
  onClose: () => void;
}

/**
 * Modal de Cadastrar/Editar simulacao antiga (FE-WINDX-043 v6 — smoke
 * turno 6 PO 2026-05-26).
 *
 * Mudancas v6:
 * - Volta Modal.Header com titulo em NEGRITO ("Cadastrar uma Simulação Antiga")
 * - Header "Data da Simulação (D-14 ate D-1)" recebe o MESMO estilo dos
 *   Accordion.Header primary (bg #ECECEC + texto #212F58 dark-blue +
 *   min-height 40px + bold) — agora alinha visualmente com "Tipo de Rodada"
 *   ao lado
 * - Label do nome: "Nome da simulação antiga" (lowercase em 'simulação')
 * - Input do nome: largura ~metade do modal (col-md-7) + contador 0/30
 *   no <InputGroup.Text> a direita (substitui o lapis). Counter de baixo
 *   removido — economia vertical de ~16px.
 */
export default function ModalCadastrar({
  open,
  initialValue,
  opts,
  dataMinima,
  dataMaxima,
  onClose,
}: ModalCadastrarProps) {
  const dispatch = useDispatch();
  const isEdit = initialValue !== undefined;
  const {
    form,
    setNome,
    setData,
    setTipoRodada,
    setIdsModelos,
    setOpcoesRmv,
    isValid,
    payload,
  } = useSimulacaoAntigaForm(initialValue);

  const [saveSimulacao, { isLoading: isSaving }] = useSaveSimulacaoAntigaMutation();
  const idsAtivos = useSelector(selectWindxIdsSimulacoesAntigasAtivas);

  const modelosVisiveis = useMemo(
    () => opts.modelos ?? [],
    [opts.modelos],
  );

  const dataIsoForm = form.data ? format(form.data, 'yyyy-MM-dd') : undefined;
  const skipModelosDisp = dataIsoForm === undefined;
  const { data: modelosResp } = useGetModelosDisponiveisQuery(
    {
      tela: 'sh',
      data_simulacao: dataIsoForm,
      tipo_rodada: form.tipoRodada,
    },
    { skip: skipModelosDisp },
  );

  const idsSemDados = useMemo<number[]>(() => {
    if (!modelosResp?.modelos) return [];
    return (modelosResp.modelos as ModeloDisponivel[])
      .filter((m) => !m.disponivel)
      .map((m) => m.id);
  }, [modelosResp]);

  const datasRodadaPorId = useMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {};
    (modelosResp?.modelos as ModeloDisponivel[] | undefined ?? []).forEach((m) => {
      if (m.data_rodada) map[m.id] = m.data_rodada;
    });
    return map;
  }, [modelosResp]);

  const opcoesRmvDisponiveis = useMemo<OpcaoRmv[]>(() => {
    if (form.idsModelos.length === 0) return ['com_rmv', 'sem_rmv'];
    const set = new Set<OpcaoRmv>();
    form.idsModelos.forEach((id) => {
      const m = modelosVisiveis.find((mm) => mm.id === id);
      m?.opcoes_rmv?.forEach((o) => set.add(o));
    });
    if (set.size === 0) return ['com_rmv', 'sem_rmv'];
    return Array.from(set);
  }, [form.idsModelos, modelosVisiveis]);

  const handleDismiss = () => {
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !payload || !form.data) return;
    try {
      const response = await saveSimulacao(payload).unwrap();
      // FE-WINDX-043 v8: apos save, marcar a simulacao como ATIVA se ela
      // ainda nao estiver. NAO dispara realizarConsulta — decisao do PO
      // 2026-05-26 (smoke turno 8): apos salvar, apenas habilita o botao
      // "Realizar Consulta" via `consultaPendente=true` (o reducer ja
      // marca isso no toggle). Usuario decide quando recarregar o grafico
      // — evita request automatico que pode demorar quando ele ainda esta
      // ajustando os filtros.
      if (!idsAtivos.includes(response.id)) {
        dispatch(toggleSimulacaoAntigaAtiva(response.id));
      }
      onClose();
    } catch (err) {
      // Erro do BE eh mostrado via toast/alert global; aqui apenas nao fecha.
    }
  };

  const nomeLen = form.nome.trim().length;
  const counterDanger = nomeLen > LIMITE_NOME;
  const salvarDisabled = !isValid || isSaving;

  return (
    <Modal
      show={open}
      onHide={handleDismiss}
      size="lg"
      centered
      className="windx-simant-modal"
      aria-labelledby="windx-simant-titulo"
    >
      <Modal.Header closeButton className="py-2">
        <Modal.Title id="windx-simant-titulo" className="h6 mb-0 fw-bold">
          {isEdit ? 'Editar Simulação Antiga' : 'Cadastrar uma Simulação Antiga'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2 pb-2 px-3">
        <Form onSubmit={handleSubmit} noValidate>
          <Form.Group className="mb-2" controlId="windx-simant-nome">
            <Form.Label className="mb-0 small">
              {`Nome da simulação antiga (até ${LIMITE_NOME} caracteres)`}
            </Form.Label>
            <Row>
              <Col md={7}>
                <InputGroup size="sm">
                  <Form.Control
                    id="windx-simant-nome"
                    placeholder="Ex.: ETA40 GFS GEFS D-3"
                    value={form.nome}
                    onChange={(e) => setNome(e.target.value)}
                    aria-label="Nome da simulação antiga"
                    // FE-WINDX-043 v8: hard-stop em 30 chars (PO 2026-05-26)
                    maxLength={LIMITE_NOME}
                  />
                  <InputGroup.Text
                    className={`bg-white ${counterDanger ? 'text-danger' : 'text-muted'}`}
                  >
                    {`${nomeLen}/${LIMITE_NOME}`}
                  </InputGroup.Text>
                </InputGroup>
              </Col>
            </Row>
          </Form.Group>

          <div className="windx-simant-card border rounded px-3 pt-2 pb-1">
            <Row className="g-2">
              <Col md={6}>
                <div
                  className="text-center fw-bold rounded mb-2 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: ACCORDION_HEADER_BG,
                    color: ACCORDION_HEADER_FG,
                    minHeight: '40px',
                    fontSize: '0.95rem',
                  }}
                >
                  Data da Simulação (D-14 até D-1)
                </div>
                <div
                  className="d-flex justify-content-center"
                  style={{ fontSize: '0.85rem' }}
                >
                  <DatePicker
                    selected={form.data ?? null}
                    onChange={(d) => setData((d as Date) ?? undefined)}
                    minDate={new Date(dataMinima.replace(/-/g, '/'))}
                    maxDate={new Date(dataMaxima.replace(/-/g, '/'))}
                    inline
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </Col>
              <Col md={6}>
                <Accordion defaultActiveKey="run-type">
                  {opts.tipos_rodada && opts.tipos_rodada.length > 0 && (
                    <RunType
                      opcoes={opts.tipos_rodada}
                      valor={form.tipoRodada}
                      onChange={setTipoRodada}
                    />
                  )}
                  <Models
                    modelos={modelosVisiveis}
                    idsSelecionados={form.idsModelos}
                    onChange={setIdsModelos}
                    idsIndisponiveis={idsSemDados}
                    datasRodadaPorId={datasRodadaPorId}
                    tipoRodada={form.tipoRodada}
                    modoSelecao="checkbox"
                  />
                  <Rmv
                    opcoesDisponiveis={opcoesRmvDisponiveis}
                    valor={form.opcoesRmv}
                    onChange={setOpcoesRmv}
                    apenasComRmv={false}
                  />
                </Accordion>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button type="secondary" icon="close" onClick={handleDismiss}>
          Cancelar
        </Button>
        <Button
          type="primary"
          icon="checkmark"
          onClick={handleSubmit}
          disabled={salvarDisabled}
        >
          {isSaving ? 'Salvando…' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
